// src/components/locutor/LocutorControl.js

import React, { useState } from 'react';
import { useLocutor } from '../../hooks/useLocutor';
import { apiFetch } from '../../apiFetch';

import { useLocutorBackend as useLocutorScheduler } from '../../hooks/useLocutorBackend';
import { DestaquesPanel }           from './DestaquesPanel';

import { LocutorStatusPanel }    from './LocutorStatusPanel';

const ChevronIcon = ({ size = 12, color = 'var(--text-muted)' }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 4l4 4 4-4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function normalizeSectionText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function highlightSectionRef(section) {
  const source = typeof section === 'string' ? { nome: section } : (section || {});
  const sectionName = String(
    source.sectionName || source.nome || source.name || source.label || source.secao || ''
  ).trim();
  const sectionId = String(source.sectionId || source.secaoId || source.id || source._id || '').trim();
  const sectionKey = String(source.sectionKey || source.secaoKey || '').trim()
    || (sectionId
      ? `section-id:${sectionId}`
      : (sectionName ? `section-name:${normalizeSectionText(sectionName)}` : ''));
  if (!sectionName || !sectionKey) return null;
  return {
    sectionKey,
    sectionId: sectionId || null,
    sectionName,
  };
}

export default function LocutorControl() {
  const { isScheduled, isPlaying, scheduleLocutor, cancelLocutor, playLocutorNow, scheduledIn, scheduledTime } = useLocutor();

  const [collapsed, setCollapsed] = useState(false);
  const [abrirIdentidade, setAbrirIdentidade] = useState(false);
  const [faixasPorLocucao, setFaixasPorLocucao] = useState(2);
  const [abrirFrequencia, setAbrirFrequencia] = useState(false);
  const [identidade, setIdentidade] = useState({
    formalidade:          0.45,
    proximidade:          0.55,
    expressividade:       0.45,
    intensidadeComercial: 0.35,
    diretividade:         0.20,
    urgencia:             0.00,
    especificidade:       0.35,
    criatividade:         0.40,
    toleranciaRepeticao:  0.30,
  });

  const scheduler = useLocutorScheduler({
    isScheduled, isPlaying, scheduleLocutor, cancelLocutor, playLocutorNow,
  });


  // Seções disponíveis para o painel de destaques
  const [secoesDisponiveis, setSecoesDisponiveis] = React.useState([]);
  React.useEffect(() => {
    const user = JSON.parse(localStorage.getItem('radio-ai-user') || '{}');
    if (!user.id) return;
    fetch(`/api/store-map/${user.id}/planta`)
      .then(r => r.json())
      .then(d => {
        const refs = (d.secoes || []).map(highlightSectionRef).filter(Boolean);
        setSecoesDisponiveis(refs);
        if (refs.length === 0) console.warn('[LocutorControl] planta sem seções cadastradas');
      })
      .catch(err => console.warn('[LocutorControl] Erro ao buscar planta:', err));
  }, []);

  // Ouve áudio gerado pelo backend scheduler e encaixa via SynchronizerLocutor
  React.useEffect(() => {
    const handler = async (e) => {
      const { audioUrl, duration, pauseMap, bedUrl, delayGeracao, executeAt } = e.detail || {};
      if (!audioUrl) return;
      if (executeAt) {
        // Backend calculou o momento exato — agenda via setTimeout
        const delayMs = Math.max(0, new Date(executeAt).getTime() - Date.now());
        console.log(`[LocutorControl] ⏱️ Agendando em ${(delayMs/1000).toFixed(1)}s (executeAt)`);
        setTimeout(() => playLocutorNow(audioUrl, pauseMap, bedUrl), delayMs);
      } else {
        // Sem executeAt (falar-agora manual ou browser controla o player)
        const audio = window.audioCoordinator?.audioElement;
        const musicaTocando = audio && isFinite(audio.duration) && !audio.paused;
        if (musicaTocando) {
          const duracaoComDelay = (duration || 12) + (delayGeracao || 0);
          console.log(`[LocutorControl] 📐 duração: ${duration}s + delay: ${(delayGeracao||0).toFixed(1)}s`);
          await scheduleLocutor(audioUrl, duracaoComDelay, pauseMap, bedUrl);
        } else {
          await playLocutorNow(audioUrl, pauseMap, bedUrl);
        }
      }
    };
    window.addEventListener('locutor-audio-pronto', handler);
    return () => window.removeEventListener('locutor-audio-pronto', handler);
  }, [scheduleLocutor, playLocutorNow]);

  React.useEffect(() => {
    const user = JSON.parse(localStorage.getItem('radio-ai-user') || '{}');
    if (!user.id) return;
    setFaixasPorLocucao(user.faixasPorLocucao || 2);
  }, []);

  React.useEffect(() => {
    if (Number.isInteger(scheduler.locutor.faixasPorLocucao)) {
      setFaixasPorLocucao(scheduler.locutor.faixasPorLocucao);
    }
  }, [scheduler.locutor.faixasPorLocucao]);

  React.useEffect(() => {
    const carregarIdentidade = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('radio-ai-user') || '{}');
        if (!user.id) return;

        const res = await fetch(`/api/locutor/identidade/${user.id}`, {
          credentials: 'include'
        });

        if (!res.ok) return;

        const data = await res.json();
        const config = data?.config || data;

        setIdentidade(prev => ({
          ...prev,
          ...config
        }));
      } catch (err) {
        console.warn('[Identidade] erro ao carregar', err);
      }
    };

    carregarIdentidade();
  }, []);

  const salvarFaixasPorLocucao = async (valor) => {
    try {
      const user = JSON.parse(localStorage.getItem('radio-ai-user') || '{}');
      if (!user.id) return;
      const res = await apiFetch(`/api/usuarios/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({ faixasPorLocucao: valor }),
      });
      if (res.ok) {
        localStorage.setItem('radio-ai-user', JSON.stringify({ ...user, faixasPorLocucao: valor }));
      }
    } catch (err) {
      console.warn('[FrequenciaLocutor] erro ao salvar', err);
    }
  };

  const salvarIdentidade = async (novo) => {
    try {
      const user = JSON.parse(localStorage.getItem('radio-ai-user') || '{}');
      if (!user.id) return;

      await fetch(`/api/locutor/identidade/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(novo)
      });
    } catch (err) {
      console.warn('[Identidade] erro ao salvar', err);
    }
  };


  return (
    <div className="config-panel">
      <div
        className="panel-header"
        onClick={() => setCollapsed(!collapsed)}
        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          Controle do Locutor
          {scheduler.locutor.ativo && <span className="locutor-live">AO VIVO</span>}
        </span>
        <span style={{ display: 'inline-flex', transition: 'transform 0.2s', transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)' }}>
          <ChevronIcon />
        </span>
      </div>

      {!collapsed && (
        <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4, minHeight: 0 }}>
          <LocutorStatusPanel
            locutor={scheduler.locutor}
            falando={scheduler.falando}
            configReady={scheduler.configReady}
            status={scheduler.status}
            ultimaFala={scheduler.ultimaFala}
            faixasDesdeUltimaLocucao={scheduler.locutor.faixasDesdeUltimaLocucao}
            faixasPorLocucao={scheduler.locutor.faixasPorLocucao}
            isScheduled={isScheduled}
            isPlaying={isPlaying}
            scheduledIn={scheduledIn}
            scheduledTime={scheduledTime}
            textoImediato={scheduler.textoImediato}
            setTextoImediato={scheduler.setTextoImediato}
            erroTextoImediato={scheduler.erroTextoImediato}
            ativarLocutorAutomatico={scheduler.ativarLocutorAutomatico}
            falarAgoraImediato={scheduler.falarAgoraImediato}
          />

          <div
            className="card-panel"
            style={{
              marginBottom: 10,
              borderColor: scheduler.locutor.indoorLocutorMode === 'FULL_AUTOMATIC'
                ? 'rgba(var(--success-rgb), 0.35)'
                : 'rgba(var(--warning-rgb), 0.55)',
            }}
          >
            <div
              className="card-header"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span>Locuções automáticas da loja</span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: scheduler.locutor.indoorLocutorMode === 'FULL_AUTOMATIC'
                    ? 'var(--success)'
                    : 'var(--warning)',
                  textTransform: 'uppercase',
                  letterSpacing: 0,
                }}
              >
                {scheduler.locutor.indoorLocutorMode === 'FULL_AUTOMATIC'
                  ? 'Completas'
                  : 'Somente destaques e eventos'}
              </span>
            </div>
            <div className="card-body" style={{ display: 'grid', gap: 8 }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                {scheduler.locutor.indoorLocutorMode === 'FULL_AUTOMATIC'
                  ? 'O locutor pode usar a escada factual da planta. Destaques e eventos seguem seus próprios cadastros.'
                  : 'Itens, categorias, posição, existência de seção e falas automáticas não factuais estão pausados. Destaques e eventos continuam funcionando.'}
              </div>
              <button
                type="button"
                onClick={() => scheduler.setIndoorLocutorMode(
                  scheduler.locutor.indoorLocutorMode === 'FULL_AUTOMATIC'
                    ? 'HIGHLIGHTS_ONLY'
                    : 'FULL_AUTOMATIC'
                )}
                disabled={scheduler.falando}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid rgba(var(--brand-rgb), 0.35)',
                  background: scheduler.locutor.indoorLocutorMode === 'FULL_AUTOMATIC'
                    ? 'rgba(var(--warning-rgb), 0.14)'
                    : 'rgba(var(--success-rgb), 0.16)',
                  color: scheduler.locutor.indoorLocutorMode === 'FULL_AUTOMATIC'
                    ? 'var(--warning)'
                    : 'var(--success)',
                  fontWeight: 700,
                  cursor: scheduler.falando ? 'not-allowed' : 'pointer',
                  opacity: scheduler.falando ? 0.55 : 1,
                }}
              >
                {scheduler.locutor.indoorLocutorMode === 'FULL_AUTOMATIC'
                  ? 'Pausar locuções automáticas'
                  : 'Reativar locuções automáticas'}
              </button>
            </div>
          </div>

          <DestaquesPanel
            clienteId={JSON.parse(localStorage.getItem('radio-ai-user') || '{}').id}
            secoesDisponiveis={secoesDisponiveis}
          />

          <div className="card-panel">
            <div
              className="card-header"
              onClick={() => setAbrirIdentidade(!abrirIdentidade)}
              style={{
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <span>Identidade do Locutor</span>
              <span style={{ display: 'inline-flex', transition: 'transform 0.2s', transform: abrirIdentidade ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                <ChevronIcon />
              </span>
            </div>

            {abrirIdentidade && (
              <div className="card-body">
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45, marginBottom: 10 }}>
                  Estes eixos ajustam apenas caminhos não factuais. Eles não reconfiguram a escada determinística nem autorizam fatos, claims ou CTA no M1.
                </div>
                {(() => {
                  const SLIDER_TOOLTIPS = {
                    formalidade:          'Coloquial vs profissional. Baixo = conversa do dia a dia. Alto = linguagem organizada e precisa.',
                    proximidade:          'Distância relacional. Baixo = narração distante. Alto = tom conversado e próximo.',
                    expressividade:       'Presença vocal. Baixo = objetivo e estável. Alto = pausas, gestos verbais, mais vivacidade.',
                    intensidadeComercial: 'Preferência de estilo para caminhos não factuais. Não autoriza elogio, oferta ou claim no M1.',
                    diretividade:         'Preferência de estilo. CTA no M1 exige ação explicitamente tipada; o controle não cria essa autorização.',
                    urgencia:             'Preferência de estilo. Urgência no M1 exige evento ou promoção declarada; o controle não cria o fato.',
                    especificidade:       'Preferência de estilo. Categoria ou produto no M1 só entra por dado tipado e associado à seção exata.',
                    criatividade:         'Liberdade verbal. Baixo = direto e seguro. Alto = imagens e variações mais expressivas.',
                    toleranciaRepeticao:  'Quarentena. Baixo = bloqueia repetição rápido. Alto = permite bordões recorrentes.',
                  };
                  return (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                      gap: 8,
                    }}>
                      {[
                        ['formalidade',          'Formalidade'],
                        ['proximidade',          'Proximidade'],
                        ['expressividade',       'Expressividade'],
                        ['intensidadeComercial', 'Intensidade Comercial'],
                        ['diretividade',         'Diretividade'],
                        ['urgencia',             'Urgência'],
                        ['especificidade',       'Especificidade'],
                        ['criatividade',         'Criatividade'],
                        ['toleranciaRepeticao',  'Repetição'],
                      ].map(([key, label]) => (
                        <div key={key} style={{ marginBottom: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>{label}</label>
                              <span
                                title={SLIDER_TOOLTIPS[key] || ''}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  width: 15, height: 15, borderRadius: '50%',
                                  background: 'var(--action-subtle)', color: 'var(--text-muted)',
                                  fontSize: 10, cursor: 'help', userSelect: 'none', flexShrink: 0,
                                }}
                              >?</span>
                            </div>
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                              {Math.round((identidade[key] ?? 0) * 100)}%
                            </span>
                          </div>
                          <input
                            type="range"
                            min="0" max="1" step="0.01"
                            value={identidade[key] ?? 0}
                            onChange={(e) => {
                              const valor = parseFloat(e.target.value);
                              const novo = { ...identidade, [key]: valor };
                              setIdentidade(novo);
                              salvarIdentidade(novo);
                            }}
                            style={{ width: '100%', accentColor: 'var(--action)' }}
                          />
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          <div className="card-panel">
            <div
              className="card-header"
              onClick={() => setAbrirFrequencia(!abrirFrequencia)}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <span>Frequência do Locutor</span>
              <span style={{ display: 'inline-flex', transition: 'transform 0.2s', transform: abrirFrequencia ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                <ChevronIcon />
              </span>
            </div>
            {abrirFrequencia && (
              <div className="card-body">
                <label style={{ display: 'block', marginBottom: 8 }}>
                  Locutor fala a cada quantas faixas?
                </label>
                <select
                  value={faixasPorLocucao}
                  onChange={(e) => {
                    const valor = parseInt(e.target.value, 10);
                    setFaixasPorLocucao(valor);
                    salvarFaixasPorLocucao(valor);
                  }}
                  style={{ width: '100%', padding: '8px', borderRadius: 6, background: 'var(--bg-deep)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                >
                  {[1, 2, 3, 4, 5, 6].map(n => (
                    <option key={n} value={n}>
                      {n === 1 ? 'Toda faixa' : `A cada ${n} faixas`}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  Valor atual: {faixasPorLocucao === 1 ? 'toda faixa' : `a cada ${faixasPorLocucao} faixas`}
                </div>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
